import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, RefreshCw, PlayCircle, Sparkles, Layers } from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import useLegendToggle from '../../utils/useLegendToggle';

/**
 * Standardisation panel — list of unit-conversion rules + apply button + AI analyze.
 */

const RULE_TYPES = ['unit_conversion', 'quality_check', 'custom'];

export default function StandardisationPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [summary, setSummary] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Click-to-toggle legend for the coverage chart (Validated / Pending).
  const covLegend = useLegendToggle();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = siteId ? `?site_id=${siteId}` : '';
      const [s, r] = await Promise.all([
        axios.get(`${API}/mrv/standardisation/summary${qs}`),
        axios.get(`${API}/mrv/standardisation/rules`),
      ]);
      setSummary(s.data);
      setRules(r.data.rules || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load standardisation data');
    } finally {
      setLoading(false);
    }
  }, [showError, siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const apply = async () => {
    try {
      const res = await axios.post(`${API}/mrv/standardisation/apply`);
      const d = res.data || {};
      showSuccess(`Processed ${d.records_processed} rows; applied ${d.rules_applied} rules; validated ${d.validated}`);
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Apply failed');
    }
  };

  const aiAnalyze = async () => {
    try {
      const res = await axios.post(`${API}/mrv/standardisation/ai-analyze`);
      setAiResult(res.data);
      showInfo(`AI flagged ${res.data?.flagged?.length || 0} suspicious rows`);
    } catch (e) {
      showError(e.response?.data?.detail || 'AI analyze failed');
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API}/mrv/standardisation/rules/${id}`);
      showSuccess('Rule deleted');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Standardisation</h2>
        <div className="flex gap-2">
          <button onClick={aiAnalyze}
                  className="px-3 py-2 text-sm font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md inline-flex items-center gap-1">
            <Sparkles className="w-4 h-4" /> AI Analyze
          </button>
          <button onClick={apply}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <PlayCircle className="w-4 h-4" /> Apply rules
          </button>
          <button onClick={() => setShowCreate(s => !s)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add rule
          </button>
          <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Total records" value={summary?.total != null ? Number(summary.total).toLocaleString() : '—'} />
        <Stat label="Validated" value={summary?.validated != null ? Number(summary.validated).toLocaleString() : '—'} accent="emerald" />
        <Stat label="Pending" value={summary?.pending != null ? Number(summary.pending).toLocaleString() : '—'} accent="amber" />
        <Stat label="Active rules" value={summary?.active_rules ?? '—'} accent="violet" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Validated vs pending donut */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Validation status</h3>
          {(summary?.total || 0) > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Validated', value: summary?.validated || 0 },
                    { name: 'Pending', value: summary?.pending || 0 },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={70}
                  label={({ name }) => name}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip formatter={v => Number(v).toLocaleString()} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="No activity records yet" />
          )}
        </div>

        {/* Records by entry method */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Records by entry method</h3>
          {(summary?.total || 0) > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Manual entry', value: summary?.native_rows || 0 },
                    { name: 'Streamed',     value: summary?.oim_rows || 0 },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={70}
                  label={({ name }) => name}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#3b82f6" />
                </Pie>
                <Tooltip formatter={v => Number(v).toLocaleString()} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="No activity records yet" />
          )}
        </div>

        {/* Recent applied logs counter */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex flex-col justify-center">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">
            Standardisations applied (last 7d)
          </div>
          <div className="text-4xl font-bold text-gray-900">{summary?.applied_last_7d ?? 0}</div>
          <div className="text-xs text-gray-500 mt-2">
            Click <em>Apply rules</em> to push pending rows through standardisation.
          </div>
        </div>
      </div>

      {/* By activity type bar chart */}
      {(summary?.by_activity_type || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Validation coverage by activity type</h3>
          <ResponsiveContainer width="100%" height={Math.max(180, summary.by_activity_type.length * 32)}>
            <BarChart
              data={summary.by_activity_type.map(r => ({
                ...r,
                pending: Math.max(0, (r.total || 0) - (r.validated || 0)),
              }))}
              layout="vertical"
              margin={{ left: 8, right: 24 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="activity_type"
                width={150}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <Tooltip
                formatter={(v, name) => [Number(v).toLocaleString(), name]}
                cursor={{ fill: '#f9fafb' }}
              />
              <Legend
                iconType="circle"
                iconSize={9}
                wrapperStyle={{ fontSize: 12 }}
                onClick={covLegend.onLegendClick}
                formatter={covLegend.legendFormatter}
              />
              <Bar dataKey="validated" stackId="cov" name="Validated" fill="#10b981" radius={[0, 0, 0, 0]}
                   hide={covLegend.isHidden('Validated')}>
                {summary.by_activity_type.map((row, i) => {
                  // Color the validated portion by activity-type category for visual variety.
                  const palette = [
                    '#10b981', // emerald — default validated
                    '#0ea5e9', // sky
                    '#3b82f6', // blue
                    '#7c3aed', // violet
                    '#ec4899', // pink
                    '#f97316', // orange
                    '#84cc16', // lime
                    '#06b6d4', // cyan
                    '#a855f7', // purple
                    '#22c55e', // green
                    '#eab308', // yellow
                    '#14b8a6', // teal
                  ];
                  return <Cell key={i} fill={palette[i % palette.length]} />;
                })}
              </Bar>
              <Bar dataKey="pending" stackId="cov" name="Pending" fill="#f59e0b" radius={[0, 4, 4, 0]}
                   hide={covLegend.isHidden('Pending')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {showCreate && <CreateRuleForm onCreated={() => { setShowCreate(false); fetchAll(); }} onCancel={() => setShowCreate(false)} />}

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Rule name</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700">From</th>
              <th className="px-3 py-2 font-semibold text-gray-700">To</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">Factor</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rules.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">No rules defined yet</td></tr>
            )}
            {rules.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{r.rule_name}</td>
                <td className="px-3 py-2 text-gray-600">{r.rule_type}</td>
                <td className="px-3 py-2 text-gray-600">{r.from_unit || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.to_unit || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.conversion_factor != null ? r.conversion_factor : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${r.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {r.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deleteRule(r.id)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {aiResult?.flagged?.length > 0 && (
        <div className="mt-4 p-4 border border-violet-200 bg-violet-50 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="font-medium text-violet-900">AI flagged rows ({aiResult.flagged.length})</span>
          </div>
          <ul className="space-y-1 text-sm">
            {aiResult.flagged.slice(0, 10).map((f, i) => (
              <li key={i} className="text-gray-700">
                <span className="font-mono text-xs text-gray-500">{(f.record_id || '').slice(0, 8)}</span>
                {' — '}
                <span className="font-medium">{f.activity_type}</span>
                {' = '}
                <span className="font-mono">{f.value}</span>
                {' '}
                <span className="px-1 text-xs bg-amber-100 text-amber-800 rounded">{f.classification}</span>
                {' '}
                <span className="text-gray-600">{f.suggested_fix}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'gray' }) {
  const map = {
    gray:    'bg-gray-50 text-gray-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber:   'bg-amber-50 text-amber-700',
    blue:    'bg-blue-50 text-blue-700',
    violet:  'bg-violet-50 text-violet-700',
  };
  return (
    <div className={`p-3 rounded border border-gray-200 ${map[accent]}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function EmptyChart({ hint }) {
  return (
    <div className="flex items-center justify-center text-center text-gray-400 py-8 text-xs">
      {hint}
    </div>
  );
}

function CreateRuleForm({ onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({
    rule_name: '', rule_type: 'unit_conversion',
    from_unit: '', to_unit: '', conversion_factor: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.rule_name) { showError('Rule name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/standardisation/rules`, {
        rule_name: form.rule_name,
        rule_type: form.rule_type,
        from_unit: form.from_unit || null,
        to_unit: form.to_unit || null,
        conversion_factor: form.conversion_factor ? parseFloat(form.conversion_factor) : null,
        description: form.description || null,
        is_active: true,
      });
      showSuccess('Rule created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <input placeholder="Rule name" value={form.rule_name}
               onChange={e => setForm(f => ({ ...f, rule_name: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <select value={form.rule_type}
                onChange={e => setForm(f => ({ ...f, rule_type: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {RULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input placeholder="From unit (e.g. m3)" value={form.from_unit}
               onChange={e => setForm(f => ({ ...f, from_unit: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="To unit (e.g. kWh)" value={form.to_unit}
               onChange={e => setForm(f => ({ ...f, to_unit: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" step="any" placeholder="Factor"
               value={form.conversion_factor}
               onChange={e => setForm(f => ({ ...f, conversion_factor: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Description" value={form.description}
               onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
